import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";

const EMPTY = {
  bank_name: "", bank_code: "", branch_name: "", branch_code: "", account_type: "普通",
  account_number: "", account_holder: "", holder_kana: "", currency: "JPY", purpose: "",
  is_default: false, start_date: "", end_date: "", is_active: true, notes: "",
};

const inputCls = "w-full border border-line bg-ink px-3 py-2.5 text-sm text-white outline-none transition-colors focus:border-accent";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

export default function AdminBankAccounts() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/bank-accounts").then((r) => setItems(r.data)).catch(() => toast.error("取得に失敗しました"));

  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/bank-accounts/${editing.id}`, form);
      else await api.post("/admin/bank-accounts", form);
      toast.success("保存しました");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (a) => {
    try {
      await api.put(`/admin/bank-accounts/${a.id}`, { ...a, is_active: !a.is_active });
      toast.success(a.is_active ? "無効にしました" : "有効にしました");
      load();
    } catch {
      toast.error("変更に失敗しました");
    }
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  return (
    <div data-testid="admin-bank-accounts-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-mincho text-2xl font-bold text-white">銀行口座の管理</h1>
          <p className="mt-1 font-mono text-[10px] tracking-[0.3em] text-gray-500">請求書の振込先などに使用します</p>
        </div>
        <button data-testid="bank-add-button" onClick={() => { setForm(EMPTY); setEditing({}); }} className="flex items-center gap-2 bg-accent px-5 py-2.5 font-display text-xs font-bold tracking-[0.15em] text-ink transition-colors hover:bg-accent-hover">
          <Plus className="h-4 w-4" />
          口座を追加
        </button>
      </div>

      <div className="mt-8 overflow-x-auto border border-line">
        <table className="w-full min-w-[800px] text-left text-sm">
          <thead>
            <tr className="border-b border-line bg-coal font-mono text-[10px] tracking-[0.2em] text-gray-500">
              <th className="px-4 py-3">金融機関</th>
              <th className="px-4 py-3">口座</th>
              <th className="px-4 py-3">名義</th>
              <th className="px-4 py-3">用途</th>
              <th className="px-4 py-3">状態</th>
              <th className="px-4 py-3">操作</th>
            </tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} data-testid={`bank-row-${a.id}`} className="border-b border-line bg-panel last:border-b-0">
                <td className="px-4 py-3 text-xs font-bold text-white">
                  {a.bank_name} {a.branch_name}
                  {a.is_default && <span className="ml-2 bg-accent px-1.5 font-mono text-[9px] font-bold text-ink">既定</span>}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-gray-400">{a.account_type} {a.account_number}</td>
                <td className="px-4 py-3 text-xs text-gray-400">{a.account_holder}</td>
                <td className="px-4 py-3 text-xs text-gray-400">{a.purpose}</td>
                <td className="px-4 py-3 text-xs">{a.is_active ? <span className="text-green-400">有効</span> : <span className="text-gray-500">無効</span>}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button data-testid={`bank-edit-${a.id}`} onClick={() => { setForm({ ...EMPTY, ...a }); setEditing(a); }} className="border border-line p-2 text-gray-400 transition-colors hover:border-accent hover:text-accent">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button data-testid={`bank-toggle-${a.id}`} onClick={() => toggleActive(a)} className={`border px-2.5 py-2 font-mono text-[10px] transition-colors ${a.is_active ? "border-line text-gray-400 hover:border-red-500 hover:text-red-400" : "border-line text-gray-400 hover:border-green-500 hover:text-green-400"}`}>
                      {a.is_active ? "無効化" : "有効化"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">口座が登録されていません。</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/80 p-4 backdrop-blur-sm">
          <form data-testid="bank-form" onSubmit={save} className="max-h-[90vh] w-full max-w-xl space-y-4 overflow-y-auto border border-line bg-coal p-8">
            <div className="flex items-center justify-between">
              <h2 className="font-mincho text-lg font-bold text-white">{editing.id ? "口座を編集" : "口座を追加"}</h2>
              <button type="button" data-testid="bank-form-close" onClick={() => setEditing(null)} className="text-gray-500 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="金融機関名 *"><input data-testid="bank-form-name" required value={form.bank_name} onChange={set("bank_name")} className={inputCls} placeholder="百五銀行" /></Field>
              <Field label="金融機関コード"><input value={form.bank_code} onChange={set("bank_code")} className={inputCls} /></Field>
              <Field label="支店名"><input value={form.branch_name} onChange={set("branch_name")} className={inputCls} placeholder="津支店" /></Field>
              <Field label="支店コード"><input value={form.branch_code} onChange={set("branch_code")} className={inputCls} /></Field>
              <Field label="口座種別">
                <select data-testid="bank-form-type" value={form.account_type} onChange={set("account_type")} className={inputCls}>
                  <option value="普通">普通</option><option value="当座">当座</option><option value="貯蓄">貯蓄</option>
                </select>
              </Field>
              <Field label="口座番号"><input data-testid="bank-form-number" value={form.account_number} onChange={set("account_number")} className={inputCls} /></Field>
              <Field label="口座名義"><input value={form.account_holder} onChange={set("account_holder")} className={inputCls} placeholder="株式会社ネオ建設" /></Field>
              <Field label="名義フリガナ"><input value={form.holder_kana} onChange={set("holder_kana")} className={inputCls} /></Field>
              <Field label="用途"><input value={form.purpose} onChange={set("purpose")} className={inputCls} placeholder="売上入金用" /></Field>
              <Field label="利用開始日"><input value={form.start_date} onChange={set("start_date")} className={inputCls} placeholder="2026-01-01" /></Field>
            </div>
            <Field label="備考"><textarea rows={2} value={form.notes} onChange={set("notes")} className={`resize-y ${inputCls}`} /></Field>
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-3 text-sm text-gray-300">
                <input data-testid="bank-form-default" type="checkbox" checked={form.is_default} onChange={set("is_default")} className="h-4 w-4 accent-accent" />
                既定の振込先にする
              </label>
              <label className="flex items-center gap-3 text-sm text-gray-300">
                <input data-testid="bank-form-active" type="checkbox" checked={form.is_active} onChange={set("is_active")} className="h-4 w-4 accent-accent" />
                有効
              </label>
            </div>
            <button data-testid="bank-form-submit" type="submit" disabled={saving} className="w-full bg-accent py-3 font-display text-sm font-bold tracking-[0.2em] text-ink transition-colors hover:bg-accent-hover disabled:opacity-50">
              {saving ? "保存中..." : "保存する"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
